import { registerSchema } from "@closer/shared";
import { Button, Card, FormField, Input, useToast } from "@closer/ui";
import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import type { z } from "zod";

import { ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { inviteReturnPath } from "../../lib/inviteReturn";
import { applyServerErrors, useZodForm } from "../../lib/useZodForm";
import { LegalConsentFields, useLegalDocuments } from "./LegalConsentFields";
import { SocialAuthButtons } from "./SocialAuthButtons";

type RegisterValues = z.infer<typeof registerSchema>;

export function RegisterPage() {
  const { user, isLoading, register: registerUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const form = useZodForm(registerSchema, {
    defaultValues: { name: "", email: "", password: "", acceptedDocumentIds: [] } as RegisterValues,
  });

  const { data: belgeler } = useLegalDocuments();
  const [secilenler, setSecilenler] = useState<string[]>([]);
  const [onayHatasi, setOnayHatasi] = useState<string | null>(null);

  const degistir = (id: string, secili: boolean): void => {
    setOnayHatasi(null);
    setSecilenler((oncekiler) => (secili ? [...oncekiler, id] : oncekiler.filter((x) => x !== id)));
  };

  /**
   * Davetten gelindiyse dönüş davet ekranına (CLOS-74 `[035]`: "Yeni hesap gerekiyorsa
   * seçilen kayıt yöntemiyle tek hesap oluşturulur"); yoksa firma seçimine.
   */
  const hedef = inviteReturnPath(location.search) ?? "/tenants";

  if (!isLoading && user) return <Navigate to={hedef} replace />;

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      // Onaylar formun kendi alanı DEĞİL, ayrı bir durum: kullanıcı kutuları
      // işaretledikçe değişir ve şema doğrulaması onları serbest bırakır. Asıl karar
      // yine SUNUCUDA veriliyor (CLOS-342) — buradaki hata yalnız kullanıcıyı erken
      // bilgilendirmek için.
      await registerUser({ ...values, acceptedDocumentIds: secilenler });
      toast.success("Hesabınız oluşturuldu");
      navigate(hedef);
    } catch (error) {
      if (error instanceof ApiRequestError) {
        const onay = error.fields?.find((f) => f.field === "acceptedDocumentIds");
        if (onay) setOnayHatasi(onay.message);
      }
      applyServerErrors(form, error);
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : "Kayıt tamamlanamadı. Lütfen tekrar deneyin.",
      );
    }
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <Card title="Kayıt Ol" className="w-full max-w-sm">
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
          <FormField label="Ad Soyad" error={form.formState.errors.name?.message} required>
            <Input autoComplete="name" {...form.register("name")} />
          </FormField>
          <FormField label="E-posta" error={form.formState.errors.email?.message} required>
            <Input type="email" autoComplete="email" {...form.register("email")} />
          </FormField>
          <FormField label="Şifre" error={form.formState.errors.password?.message} required>
            <Input type="password" autoComplete="new-password" {...form.register("password")} />
          </FormField>
          <LegalConsentFields
            belgeler={belgeler ?? []}
            secilenler={secilenler}
            degistir={degistir}
            hata={onayHatasi ?? undefined}
          />
          <Button type="submit" loading={form.formState.isSubmitting}>
            Kayıt Ol
          </Button>
          <SocialAuthButtons label="Google ile devam et" />
          <p className="text-center text-sm text-slate-500">
            Zaten hesabınız var mı?{" "}
            {/* Davet bilgisi karşı sayfaya da taşınıyor (LoginPage'deki gerekçenin aynısı). */}
            <Link
              to={`/login${location.search}`}
              className="font-medium text-primary hover:underline"
            >
              Giriş yapın
            </Link>
          </p>
        </form>
      </Card>
    </div>
  );
}
